<template>
  <v-dialog v-model="dialog" persistent max-width="600px" :fullscreen="xsmall">
    <v-card class="d-flex flex-column">
      <v-progress-linear
        :active="isLoading"
        :indeterminate="isLoading"
        absolute
        top
        color="secondary"
      >
      </v-progress-linear>
      <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <form
          class="flex-fill d-flex flex-column"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <v-card-title flat rounded>
            <v-toolbar-title v-if="isNew">Add Client</v-toolbar-title>
            <v-toolbar-title v-else>Edit Client</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon :disabled="isLoading" @click="dialog = false">
              <base-icon name="times"></base-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-3 flex-fill">
            <v-row>
              <v-col cols="12">
                <validation-provider
                  v-slot="{ errors, failed }"
                  name="Name"
                  rules="required|min:2|max:100"
                >
                  <v-text-field
                    v-model="client.Name"
                    label="Name"
                    outlined
                    :disabled="isLoading"
                    :error-messages="errors"
                    :append-icon="failed ? '$error' : ''"
                    :class="{ 'in-error': failed }"
                  >
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="2">
                <v-btn
                  color="grey darken-3 white--text"
                  height="54px"
                  type="button"
                  @click="copyToClipboard(client.ClientId)"
                >
                  <v-icon medium>mdi-content-copy</v-icon> Copy
                </v-btn>
              </v-col>
              <v-col cols="10">
                <v-text-field
                  v-model="client.ClientId"
                  label="Client Id (System Generated)"
                  outlined
                  filled
                  width="400px"
                  :autofocus="isNew"
                  disabled
                  placeholder="System Generated"
                >
                </v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <div v-if="isNew && client.ClientSecret === ''" key="generate">
                  <v-btn
                    color="yellow accent-4"
                    type="button"
                    @click="generateSecret"
                  >
                    Create Secret
                  </v-btn>
                </div>
                <div v-else key="display" transition="slide-x-transition">
                  <v-row>
                    <v-col cols="2">
                      <v-btn
                        color="grey darken-3 white--text"
                        height="54px"
                        type="button"
                        @click="copyToClipboard(client.ClientSecret)"
                      >
                        <v-icon medium>mdi-content-copy</v-icon> Copy
                      </v-btn>
                    </v-col>
                    <v-col cols="7">
                      <v-text-field
                        v-model="client.ClientSecret"
                        label="Secret"
                        outlined
                        filled
                        disabled
                      >
                      </v-text-field>
                    </v-col>
                    <v-col cols="3">
                      <v-btn
                        color="yellow accent-4"
                        height="54px"
                        type="button"
                        @click="generateSecret"
                      >
                        Recreate
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <validation-provider
                  v-slot="{ errors, failed }"
                  name="Description"
                  rules="required|min:2|max:500"
                >
                  <v-textarea
                    v-model="client.Description"
                    label="Description"
                    outlined
                    :disabled="isLoading"
                    :counter="500"
                    :error-messages="errors"
                    :append-icon="failed ? '$error' : ''"
                    :class="{ 'in-error': failed }"
                  >
                  </v-textarea>
                </validation-provider>
              </v-col>
            </v-row>
          </v-card-text>
          <v-expansion-panels v-model="panels" flat class="dense">
            <v-expansion-panel>
              <dashed-divider class="mx-4"></dashed-divider>
              <v-expansion-panel-header class="text-h6">
                <div class="d-flex align-center pr-2">
                  <span class="flex-fill">Permissions</span>
                  <v-scale-transition origin="center" mode="out-in">
                    <v-badge inline :content="'' + client.ClientScopes.length">
                    </v-badge>
                  </v-scale-transition>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list flat>
                  <v-list-item-group>
                    <v-list-item
                      v-for="scope in scopes"
                      :key="scope"
                      @click="toggleScope(scope)"
                    >
                        <v-list-item-action>
                          <base-icon
                            v-if="hasScope(scope)"
                            name="check-square"
                            class="primary--text"
                          ></base-icon>
                          <base-icon
                            v-else
                            :name="['far', 'square']"
                          ></base-icon>
                        </v-list-item-action>

                        <v-list-item-content>
                          <v-list-item-title>{{ scope }}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <dashed-divider class="mx-4"></dashed-divider>
              <v-expansion-panel-header class="text-h6">
                <div class="d-flex align-center pr-2">
                  <span class="flex-fill">Claims</span>
                  <v-scale-transition origin="center" mode="out-in">
                    <v-badge inline :content="'' + claimsCount"> </v-badge>
                  </v-scale-transition>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card-text>
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors, failed }"
                      name="Email"
                      rules="min:2|max:255"
                    >
                      <v-text-field
                        v-model="client.ClientClaims.email"
                        label="Email"
                        placeholder="Email"
                        outlined
                        type="email"
                        :disabled="isLoading"
                        :error-messages="errors"
                        :append-icon="failed ? '$error' : ''"
                        :class="{ 'in-error': failed }"
                      >
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors, failed }"
                      name="ContactName"
                      rules="min:2|max:255"
                    >
                      <v-text-field
                        v-model="client.ClientClaims.contactName"
                        name="Contact Name"
                        label="Contact Name"
                        placeholder="Contact Name"
                        outlined
                        type="text"
                        :disabled="isLoading"
                        :error-messages="errors"
                        :append-icon="failed ? '$error' : ''"
                        :class="{ 'in-error': failed }"
                      >
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-card-text>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-card-actions :class="{ 'flex-column-reverse': xsmall }">
            <v-spacer></v-spacer>
            <v-btn
              :text="!xsmall"
              :outlined="xsmall"
              :block="xsmall"
              :class="{ 'mt-2': xsmall }"
              :disabled="isLoading"
              @click="dialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              type="submit"
              color="primary"
              elevation="0"
              :disabled="isLoading || client.ClientSecret === ''"
              :block="xsmall"
              :class="{ 'ma-0': xsmall }"
              :loading="isLoading"
            >
              {{ isNew ? 'Add' : 'Save' }}
            </v-btn>
          </v-card-actions>
        </form>
      </validation-observer>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'

const formDefault = () => ({
  ClientId: '',
  Name: '',
  Description: '',
  ClientSecret: '',
  ClientScopes: [],
  ClientClaims: {
    email: '',
    contactName: '',
  },
})

export default {
  props: {
    clients: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      isLoading: false,
      client: formDefault(),
      isNew: false,
      query: '',
      panels: [0],
    }
  },
  computed: {
    xsmall() {
      return this.$vuetify.breakpoint.xs
    },
    scopes() {
      return this.$store.state.approvalGroup.scopes
    },
    claimsCount() {
      return Object.keys(this.claimsGiven).length
    },
    claimsGiven() {
      return Object.fromEntries(
        Object.entries(this.client.ClientClaims).filter(
          (value) => value !== undefined && value !== null && value !== ''
        )
      )
    },
  },
  mounted() {
    this.$store.dispatch('approvalGroup/loadScopes')
  },
  methods: {
    open(initialState = {}) {
      this.isNew = initialState.ClientId === undefined

      const defaultState = formDefault()
      this.client = defaultState

      const allowedKeys = []
      for (const key in defaultState) {
        allowedKeys.push(key)
      }

      initialState = JSON.parse(JSON.stringify(initialState))

      for (const key in initialState) {
        if (key === 'ClientClaims') {
          initialState.ClientClaims.map((x) => {
            const parts = x.split(':')
            Vue.set(this.client.ClientClaims, parts[0], parts[1])
          })
          continue
        }

        if (allowedKeys.includes(key))
          Vue.set(this.client, key, initialState[key])
      }

      this.$nextTick(() => {
        if (this.$refs.observer) this.$refs.observer.reset()
        this.dialog = true
      })
    },
    hasScope(scope) {
      return this.client.ClientScopes.some((x) => x === scope)
    },
    toggleScope(scope) {
      const i = this.client.ClientScopes.findIndex((x) => x === scope)

      if (i === -1) this.client.ClientScopes.push(scope)
      else this.client.ClientScopes.splice(i, 1)
    },
    onSubmit() {
      this.isLoading = true

      this.$store
        .dispatch('client/upsert', {
          ClientId: this.client.ClientId === '' ? null : this.client.ClientId,
          Name: this.client.Name,
          ClientSecret: this.client.ClientSecret,
          Description: this.client.Description,
          ClientScopes: this.client.ClientScopes,
          ClientClaims: Object.entries(this.claimsGiven).flatMap((x) =>
            x.join(':')
          ),
        })
        .then(
          (success) => {
            this.client.ClientId = success.ClientId
            this.client.isNew = false
            this.$refs.observer.reset()
            this.$store.dispatch('messaging/notify', {
              type: 'success',
              message: `Successfully saved.`,
              duration: 2000,
            })

            this.$emit('refresh')

            this.dialog = false
          },
          (error) => {
            switch (error.errorCode) {
              case 400: {
                this.$refs.observer.setErrors(error.data)
                break
              }
              default: {
                this.$store.dispatch('messaging/errorNotification', {
                  errorCode: error.errorCode,
                  unhandledMessage: `An unexpected error occurred while saving. Please try again later.`,
                })
              }
            }
          }
        )
        .finally(() => (this.isLoading = false))
    },
    generateSecret() {
      this.$store.dispatch('client/generateSecret').then(
        (success) => {
          this.client.ClientSecret = success
        },
        (error) => {
          switch (error.errorCode) {
            default: {
              this.$store.dispatch('messaging/errorNotification', {
                errorCode: error.errorCode,
                unhandledMessage: `An unexpected error occurred while generating secret.`,
              })
            }
          }
        }
      )
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
    },
  },
}
</script>
